import { addDays, addMonths, endOfMonth, format, startOfDay } from "date-fns"
import type { BadgeProps } from "@/v2-backoffice-client/components/badge"
import {
  PAYMENT_HEALTH_NONPAYMENT,
  PAYMENT_HEALTH_OVERDUE,
} from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import type { RenewalTimelineQuery } from "@/v2-backoffice-client/license/renewals/__types__/license.renewals.query"

export type RenewalTimelineItem =
  RenewalTimelineQuery["getRenewalTimeline"][number]

/** Default projection window; the board offers 12 and 24 months. */
export const RENEWAL_HORIZON_MONTHS = 12

/** Chart bucket granularity. */
export type RenewalGranularity = "month" | "quarter"

// Fenerum's subscription statuses, verbatim (lic.LicenseSubscriptions).
const STATUS_NOT_RENEWING = "not_renewing"
const STATUS_SCHEDULED = "scheduled"

export const RENEWAL_EVENT_KINDS = {
  /** A committed renewal — the term invoice that will land. */
  RENEWAL: "Renewal",
  /** A renewal of a customer currently behind on payment. */
  AT_RISK: "At risk",
  /** A scheduled subscription's cash entering the book. */
  STARTING: "Starting",
  /** The renewal that will NOT happen — cash leaving the book. */
  CHURNING: "Churning",
} as const

export type RenewalEventKind =
  (typeof RENEWAL_EVENT_KINDS)[keyof typeof RENEWAL_EVENT_KINDS]

export type RenewalEventKindDisplay = {
  variant: BadgeProps["variant"]
  /** What the event means — the hover explanation. */
  description: string
}

// One entry per event kind: color AND meaning, so the badge explains itself
// on hover — and the chart's series wear the same colors.
export const RENEWAL_EVENT_KIND_DISPLAY: Record<
  RenewalEventKind,
  RenewalEventKindDisplay
> = {
  [RENEWAL_EVENT_KINDS.RENEWAL]: {
    variant: "positive",
    description:
      "A committed renewal: the subscription auto-renews on its Fenerum term unless cancelled before the date. The amount is the term invoice that lands.",
  },
  [RENEWAL_EVENT_KINDS.AT_RISK]: {
    variant: "warning",
    description:
      "A renewal of a customer currently behind on payment (overdue or in nonpayment) — the cash may not arrive. The Collections board carries the conversation.",
  },
  [RENEWAL_EVENT_KINDS.STARTING]: {
    variant: "positive-strong",
    description:
      "Cash from a scheduled subscription — new committed cashflow entering the book (colored as new for its whole first year).",
  },
  [RENEWAL_EVENT_KINDS.CHURNING]: {
    variant: "critical",
    description:
      "A renewal that will NOT happen: the subscription is set not to renew in Fenerum. The amount is the cash leaving the book at that date.",
  },
}

export type RenewalEvent = {
  /** Unique per subscription × date. */
  key: string
  date: Date
  /** ISO month key, e.g. "2026-09" — the chart's bucket. */
  monthKey: string
  kind: RenewalEventKind
  /** Cash of the event (positive; for Churning: the cash that will not land). */
  amount: number
  item: RenewalTimelineItem
}

export type RenewalBucket = {
  /** Bucket key: the first month's ISO key. */
  key: string
  /** Axis label, e.g. "Sep 26" or "Q4 26". */
  label: string
  /** The ISO month keys the bucket spans (1 for monthly, 3 for quarterly). */
  monthKeys: string[]
  renewing: number
  atRisk: number
  starting: number
  /** Negative — rendered below the axis. */
  churning: number
  /** Projected monthly run-rate at the end of the bucket, after starts and churns. */
  runRate: number
  /** Running total of cash expected up to and including this bucket. */
  cumulativeIn: number
  /** Running total of cash that will NOT land, negative. */
  cumulativeChurn: number
  eventCount: number
}

// The operator context behind a subscription's row — most valuable on
// churn (Fenerum records no cancellation reason, so the app's own domain
// explains it), but a live renewal on a deprecated license is worth a marker
// too.
export type RenewalContext = {
  label: string
  variant: BadgeProps["variant"]
  description: string
}

export function getRenewalContext(
  item: RenewalTimelineItem,
): RenewalContext | null {
  if (item.MigrationStatusName) {
    return {
      label: `Migration: ${item.MigrationStatusName}`,
      variant: "warning",
      description:
        "The license is being retired and this customer has a migration conversation on the license panel's Migration tab — the churn is (or becomes) a move, not a loss.",
    }
  }
  if (item.PlanDeprecatedAt) {
    return {
      label: "License deprecated",
      variant: "warning",
      description:
        "The license is deprecated but no migration conversation is recorded yet — start one from the license panel's Migration tab.",
    }
  }
  if (
    item.PaymentHealth === PAYMENT_HEALTH_OVERDUE ||
    item.PaymentHealth === PAYMENT_HEALTH_NONPAYMENT
  ) {
    return {
      label: "Behind on payment",
      variant: "critical",
      description:
        "The customer is behind on billing (overdue or in nonpayment) — the Collections board carries the conversation.",
    }
  }
  if (item.ExternalStatus === STATUS_NOT_RENEWING) {
    return {
      label: "No reason recorded",
      variant: "neutral",
      description:
        "Set not to renew in Fenerum with nothing in our systems explaining why — worth asking the account owner.",
    }
  }
  return null
}

function toMonthKey(date: Date) {
  return format(date, "yyyy-MM")
}

function getEventKind(item: RenewalTimelineItem): RenewalEventKind {
  if (item.ExternalStatus === STATUS_NOT_RENEWING)
    return RENEWAL_EVENT_KINDS.CHURNING
  if (item.ExternalStatus === STATUS_SCHEDULED)
    return RENEWAL_EVENT_KINDS.STARTING
  if (
    item.PaymentHealth === PAYMENT_HEALTH_OVERDUE ||
    item.PaymentHealth === PAYMENT_HEALTH_NONPAYMENT
  )
    return RENEWAL_EVENT_KINDS.AT_RISK
  return RENEWAL_EVENT_KINDS.RENEWAL
}

/**
 * Projects the subscription facts onto concrete cash events inside the
 * horizon: active subscriptions renew every TermMonths from their next
 * renewal, scheduled ones start at StartDate and keep their "new money"
 * color for the window, and not_renewing ones contribute the single renewal
 * that will NOT happen. Events before today are excluded — they are actuals,
 * not forecast.
 */
export function buildRenewalEvents(
  items: RenewalTimelineItem[],
  horizonMonths = RENEWAL_HORIZON_MONTHS,
  now = new Date(),
): RenewalEvent[] {
  const today = startOfDay(now)
  const horizon = addMonths(today, horizonMonths)
  const events: RenewalEvent[] = []

  for (const item of items) {
    const kind = getEventKind(item)
    const termMonths = Math.max(1, item.TermMonths)

    if (kind === RENEWAL_EVENT_KINDS.CHURNING) {
      if (!item.NextRenewalDate) continue
      const date = new Date(String(item.NextRenewalDate))
      if (date < today || date >= horizon) continue
      events.push({
        key: `${item.SubscriptionUuid}:${toMonthKey(date)}`,
        date,
        monthKey: toMonthKey(date),
        kind,
        amount: item.PeriodAmount,
        item,
      })
      continue
    }

    const anchor =
      kind === RENEWAL_EVENT_KINDS.STARTING
        ? (item.StartDate ?? item.NextRenewalDate)
        : (item.NextRenewalDate ?? item.StartDate)
    if (!anchor) continue

    let date = new Date(String(anchor))
    // A stale anchor in the past (mirror lag) steps forward to its next
    // occurrence instead of being dropped.
    while (date < today) date = addMonths(date, termMonths)

    while (date < horizon) {
      events.push({
        key: `${item.SubscriptionUuid}:${toMonthKey(date)}`,
        date,
        monthKey: toMonthKey(date),
        kind,
        amount: item.PeriodAmount,
        item,
      })
      date = addMonths(date, termMonths)
    }
  }

  return events.sort((a, b) => a.date.getTime() - b.date.getTime())
}

/**
 * Buckets the events into the horizon (monthly or quarterly) and walks the
 * run-rate line: the currently billing book's MRR, plus scheduled MRR as it
 * starts, minus churned MRR once its final term lapses. Cumulative series
 * ride along for the chart's cumulative view.
 */
export function buildRenewalBuckets(
  items: RenewalTimelineItem[],
  events: RenewalEvent[],
  options: { horizonMonths: number; granularity: RenewalGranularity },
  now = new Date(),
): RenewalBucket[] {
  const { horizonMonths, granularity } = options
  const monthsPerBucket = granularity === "quarter" ? 3 : 1

  const buckets: RenewalBucket[] = []
  const bucketByMonthKey = new Map<string, RenewalBucket>()
  for (let ahead = 0; ahead < horizonMonths; ahead += monthsPerBucket) {
    const bucketStart = addMonths(now, ahead)
    const monthKeys: string[] = []
    for (
      let inner = 0;
      inner < monthsPerBucket && ahead + inner < horizonMonths;
      inner++
    ) {
      monthKeys.push(toMonthKey(addMonths(now, ahead + inner)))
    }
    const bucket: RenewalBucket = {
      key: monthKeys[0],
      label:
        granularity === "quarter"
          ? `${format(bucketStart, "MMM")}–${format(addMonths(bucketStart, monthKeys.length - 1), "MMM yy")}`
          : format(bucketStart, "MMM yy"),
      monthKeys,
      renewing: 0,
      atRisk: 0,
      starting: 0,
      churning: 0,
      runRate: 0,
      cumulativeIn: 0,
      cumulativeChurn: 0,
      eventCount: 0,
    }
    buckets.push(bucket)
    for (const monthKey of monthKeys) bucketByMonthKey.set(monthKey, bucket)
  }

  for (const event of events) {
    const bucket = bucketByMonthKey.get(event.monthKey)
    if (!bucket) continue
    bucket.eventCount += 1
    if (event.kind === RENEWAL_EVENT_KINDS.RENEWAL)
      bucket.renewing += event.amount
    else if (event.kind === RENEWAL_EVENT_KINDS.AT_RISK)
      bucket.atRisk += event.amount
    else if (event.kind === RENEWAL_EVENT_KINDS.STARTING)
      bucket.starting += event.amount
    else bucket.churning -= event.amount
  }

  // Run-rate walk: what the book bills per month once each bucket's starts
  // and churns have happened.
  const baseMrr = items
    .filter((item) => item.ExternalStatus !== STATUS_SCHEDULED)
    .reduce((sum, item) => sum + item.Mrr, 0)

  let cumulativeIn = 0
  let cumulativeChurn = 0
  for (const bucket of buckets) {
    const lastMonthKey = bucket.monthKeys[bucket.monthKeys.length - 1]
    const bucketEnd = endOfMonth(new Date(`${lastMonthKey}-01T00:00:00`))
    const startedMrr = items
      .filter(
        (item) =>
          item.ExternalStatus === STATUS_SCHEDULED &&
          item.StartDate &&
          new Date(String(item.StartDate)) <= bucketEnd,
      )
      .reduce((sum, item) => sum + item.Mrr, 0)
    const churnedMrr = items
      .filter(
        (item) =>
          item.ExternalStatus === STATUS_NOT_RENEWING &&
          item.NextRenewalDate &&
          new Date(String(item.NextRenewalDate)) <= bucketEnd,
      )
      .reduce((sum, item) => sum + item.Mrr, 0)
    bucket.runRate = baseMrr + startedMrr - churnedMrr

    cumulativeIn += bucket.renewing + bucket.atRisk + bucket.starting
    cumulativeChurn += bucket.churning
    bucket.cumulativeIn = cumulativeIn
    bucket.cumulativeChurn = cumulativeChurn
  }

  return buckets
}

export type RenewalStats = {
  next90Amount: number
  next90Count: number
  startingMrr: number
  startingCount: number
  firstStartDate: Date | null
  churningMrr: number
  churningCount: number
  firstChurnDate: Date | null
  lastChurnDate: Date | null
  runRateNow: number
  runRateEnd: number
  horizonMonths: number
  /** The rows' single currency, or "mixed" when they disagree. */
  currency: string
}

/** The headline numbers over the chart. */
export function buildRenewalStats(
  items: RenewalTimelineItem[],
  events: RenewalEvent[],
  buckets: RenewalBucket[],
  horizonMonths: number,
  now = new Date(),
): RenewalStats {
  const in90Days = addDays(startOfDay(now), 90)
  const cashEvents = events.filter(
    (event) =>
      event.kind !== RENEWAL_EVENT_KINDS.CHURNING && event.date <= in90Days,
  )

  const scheduled = items.filter(
    (item) => item.ExternalStatus === STATUS_SCHEDULED,
  )
  const churning = items.filter(
    (item) => item.ExternalStatus === STATUS_NOT_RENEWING,
  )

  const startDates = scheduled
    .map((item) => (item.StartDate ? new Date(String(item.StartDate)) : null))
    .filter((date): date is Date => date != null)
    .sort((a, b) => a.getTime() - b.getTime())
  const churnDates = churning
    .map((item) =>
      item.NextRenewalDate ? new Date(String(item.NextRenewalDate)) : null,
    )
    .filter((date): date is Date => date != null)
    .sort((a, b) => a.getTime() - b.getTime())

  const currencies = new Set(
    items.map((item) => item.Currency).filter(Boolean) as string[],
  )

  return {
    next90Amount: cashEvents.reduce((sum, event) => sum + event.amount, 0),
    next90Count: cashEvents.length,
    startingMrr: scheduled.reduce((sum, item) => sum + item.Mrr, 0),
    startingCount: scheduled.length,
    firstStartDate: startDates[0] ?? null,
    churningMrr: churning.reduce((sum, item) => sum + item.Mrr, 0),
    churningCount: churning.length,
    firstChurnDate: churnDates[0] ?? null,
    lastChurnDate: churnDates[churnDates.length - 1] ?? null,
    runRateNow: buckets[0]?.runRate ?? 0,
    runRateEnd: buckets[buckets.length - 1]?.runRate ?? 0,
    horizonMonths,
    currency:
      currencies.size === 1
        ? [...currencies][0]
        : currencies.size
          ? "mixed"
          : "",
  }
}
